import { Immutable } from 'immer';
import { CanonicalSchemaDefinitionField } from '../canonicalSchemas/canonicalSchemaUtils';
import { ReferenceDataSchemaDefinition } from '../referenceData/ReferenceDataSchemaInterface';
import { parseUnitString } from '../unit';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import { BadInputError } from '@watershed/errors/BadInputError';
import assertNever from '@watershed/shared-util/assertNever';
import { ASTExpr } from './ast';
import { RowExprType, RowType } from './row_types';
import { Context, SqlExpression } from './types';
import { DEFAULT_INPUT_TABLE } from './sql/internal';

export function emptyContext(): Context {
  return {
    variables: new Map(),
    variablesType: new Map(),
    input: new Map(),
    inputTable: DEFAULT_INPUT_TABLE,
    inputType: new Map(),
    refData: new Map(),
    refTypes: new Map(),
    refUniqueKeys: new Map(),
    useTypecheckStrictMode: false,
    useDateCoalesceOptimization: true,
  };
}

export function constructRowTyper(
  schemas: Map<string, ReferenceDataSchemaDefinition>,
  refTypes: Map<string, RowType>,
  refUniqueKeys: Map<string, Array<string>>
) {
  return (cols: Set<string>, slug: string): void => {
    const schema = schemas.get(slug);
    if (schema) {
      const rowType: RowType = new Map<string, RowExprType>();
      cols.forEach((col) => {
        const prop = schema.properties[col] as CanonicalSchemaDefinitionField;
        if (prop && prop.type) {
          let type: RowExprType | null = null;
          switch (prop.type) {
            case 'string':
              if (prop.format === 'date') {
                type = { ty: 'date' };
              } else if (prop.format === 'date-time') {
                type = { ty: 'datetime' };
              } else {
                type = { ty: 'string' };
              }
              break;
            case 'number':
            case 'integer':
              if (prop.unit) {
                type = {
                  ty: 'number',
                  unit: parseUnitString(prop.unit),
                };
              } else if (prop.unitColumn) {
                const unitProp = schema.properties[prop.unitColumn];
                if (unitProp && unitProp.enum) {
                  const encodedValuesToUnits = new Map(
                    unitProp.enum.map((enumValue) => [
                      enumValue,
                      parseUnitString(enumValue),
                    ])
                  );
                  type = {
                    ty: 'quantity',
                    units: Array.from(encodedValuesToUnits.values()),
                    encodedValuesToUnits,
                    column: col,
                  };
                } else {
                  type = { ty: 'number' };
                }
              } else {
                type = { ty: 'number' };
              }
              break;
            case 'boolean':
              type = { ty: 'boolean' };
              break;
            default:
              assertNever(prop.type);
          }

          if (!type) return;

          if (schema.required && schema.required.includes(col)) {
            rowType.set(col, type);
          } else {
            rowType.set(col, { ty: 'nullable', baseTy: type });
          }
        }
      });
      refTypes.set(slug, rowType);
      if (schema.uniqueKeys) {
        refUniqueKeys.set(slug, schema.uniqueKeys);
      }
    }
  };
}
export function debugAssert(
  trueFact: boolean,
  msg: () => string
): asserts trueFact {
  if (!trueFact) {
    throw new Error(`internal error: ${msg()}`);
  }
}

/**
 * Recurse through an expression and accumulate names of called functions
 */
export function functionNamesInExpression(
  expr: ASTExpr,
  seenFunctionNames: Set<string> = new Set()
): Set<string> {
  // the call, unary, lookup, binary, and comparison expression types
  // contain sub-expressions; accumulate them for deeper checks
  const nextExprs: Array<ASTExpr> = [];
  if (expr.t === 'call') {
    seenFunctionNames.add(expr.name);
    nextExprs.push(
      ...expr.args,
      ...Array.from(expr.kwargs.values()).map(({ expr }) => expr)
    );
  } else if (expr.t === 'unary') {
    nextExprs.push(expr.expr);
  } else if (expr.t === 'binary' || expr.t === 'comparison') {
    nextExprs.push(expr.r, expr.l);
  } else if (
    expr.t === 'number' ||
    expr.t === 'string' ||
    expr.t === 'boolean' ||
    expr.t === 'identifier' ||
    expr.t === 'null'
  ) {
    // these have no sub-expressions
  } else {
    // guard against unchecked new expression types that may be added
    assertNever(expr);
  }

  nextExprs.forEach((x) => {
    functionNamesInExpression(x, seenFunctionNames).forEach((f) =>
      seenFunctionNames.add(f)
    );
  });

  return seenFunctionNames;
}

/**
 * Recurse through an expression and accumulate names of referenced identifiers
 */
export function identifierNamesInExpression(
  expr: ASTExpr,
  seenIdentifierNames: Set<string> = new Set()
): Set<string> {
  // the call, unary, lookup, binary, and comparison expression types
  // contain sub-expressions; accumulate them for deeper checks
  const nextExprs: Array<ASTExpr> = [];
  if (expr.t === 'identifier') {
    seenIdentifierNames.add(expr.name);
  } else if (expr.t === 'call') {
    nextExprs.push(
      ...expr.args,
      ...Array.from(expr.kwargs.values()).map(({ expr }) => expr)
    );
  } else if (expr.t === 'unary') {
    nextExprs.push(expr.expr);
  } else if (expr.t === 'binary' || expr.t === 'comparison') {
    nextExprs.push(expr.r, expr.l);
  } else if (
    expr.t === 'number' ||
    expr.t === 'string' ||
    expr.t === 'boolean' ||
    expr.t === 'null'
  ) {
    // these have no sub-expressions
  } else {
    // guard against unchecked new expression types that may be added
    assertNever(expr);
  }

  nextExprs.forEach((x) => {
    identifierNamesInExpression(x, seenIdentifierNames).forEach((f) =>
      seenIdentifierNames.add(f)
    );
  });

  return seenIdentifierNames;
}

// This is here because regular Math.max() will silently return NaN if any of the
// arguments are NaN or undefined. This is not what we want, but while lodash's
// max doesn't do this, we want to log something if we get NaNs so we can fix it.
export function safeMaxStage(
  rawArgs:
    | Immutable<Array<SqlExpression | number>>
    | IterableIterator<SqlExpression>,
  defaultStage?: number
): number {
  const args = Array.from(rawArgs);

  if (args.length === 0) {
    if (typeof defaultStage === 'number') {
      return defaultStage;
    }
    throw new UnexpectedError('safeMax got no arguments');
  }

  return Math.max(
    ...args.map((value: { stage: number } | number): number => {
      const stage = (typeof value === 'number' ? value : value.stage) as number;

      if (typeof stage !== 'number') {
        throw new UnexpectedError(`safeMax got non-number stage: ${stage}`, {
          data: { args },
        });
      }
      return stage;
    })
  );
}

export type SlugParts = {
  referenceDataName: string;
  referenceDataVersionName: string | null;
};

export type ResolvedSlugParts = {
  referenceDataName: string;
  referenceDataVersionName: string;
};

const slugRegex = /^(?:r!)?([0-9a-z_]+)(?::([0-9a-z_]+))?$/i;

export function parseSlug(slug: string): SlugParts {
  const m = slug.match(slugRegex);
  if (m == null) {
    throw new BadInputError(
      `Invalid slug: ${slug}. Expected a string like "ecoinvent_391_freight" or "defra_waste_2017_and_2018:initial_version"`
    );
  }
  return {
    referenceDataName: m[1],
    referenceDataVersionName: m[2] ?? null,
  };
}

export function assembleSlug(parts: SlugParts): string {
  if (parts.referenceDataVersionName) {
    return `${parts.referenceDataName}:${parts.referenceDataVersionName}`;
  } else {
    return parts.referenceDataName;
  }
}
